import { useEffect, useState } from 'preact/hooks';
import {
  absoluteImageUrl,
  cancelOrder,
  createOrderChangeRequest,
  downloadOrderDocument,
  fetchOrderDetails,
} from '@/api';
import { Modal } from '@/ui/Modal.jsx';
import { Lightbox } from '@/ui/Lightbox.jsx';
import { StatusPill } from '@/ui/StatusPill.jsx';
import { PdfIcon } from '@/ui/Icons.jsx';
import { numberFormat, formatDate } from '@/utils/format';
import { notify } from '@/store/ui';
import { ProductModal } from '@/features/catalog/ProductModal.jsx';
import { MarkingModal } from './MarkingModal.jsx';
import { isMobile } from '@/lib/viewport';
import css from './OrderModal.module.css';

export function OrderModal({ orderId, onClose, onAfterChange }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [markingItem, setMarkingItem] = useState(null);
  const [productSku, setProductSku] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchOrderDetails(orderId)
      .then((data) => {
        if (!cancel) setOrder(data.order);
      })
      .catch((e) => {
        if (!cancel) setErr(e);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [orderId]);

  async function handleCancel() {
    if (!confirm('Вы уверены, что хотите отменить заказ?')) return;
    setBusy(true);
    try {
      await cancelOrder(orderId);
      notify('Заказ отменён');
      onAfterChange?.();
      onClose?.();
    } catch (e) {
      notify(e.message || 'Ошибка отмены заказа');
    } finally {
      setBusy(false);
    }
  }

  async function handleChangeRequest(type) {
    const promptText = type === 'cancel' ? 'Укажите причину отмены (опционально):' : 'Опишите, что нужно изменить:';
    const details = prompt(promptText);
    if (type === 'modify' && !details) return;
    setBusy(true);
    try {
      await createOrderChangeRequest(orderId, type, details ?? '');
      notify(type === 'cancel' ? 'Запрос на отмену отправлен менеджеру' : 'Запрос на изменение отправлен менеджеру');
      onAfterChange?.();
      onClose?.();
    } catch (e) {
      notify(e.message || 'Ошибка отправки запроса');
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(type) {
    try {
      const blob = await downloadOrderDocument(orderId, type);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${type === 'invoice' ? 'Счёт' : 'УПД'}_заказ_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      notify(e.message || 'Ошибка скачивания');
    }
  }

  return (
    <>
      <Modal open title={`Заказ #${orderId}`} size="lg" onClose={onClose}>
        {loading && <p class="loading">Загрузка</p>}
        {err && <div class="empty-state">Ошибка: {err.message}</div>}
        {order && (
          <>
            <div class={css.detailsHeader}>
              <div class={css.detailItem}>
                <span class={css.detailLabel}>Статус</span>
                <span class={css.detailValue}><StatusPill status={order.status} /></span>
              </div>
              <div class={css.detailItem}>
                <span class={css.detailLabel}>Дата создания</span>
                <span class={css.detailValue}>{formatDate(order.created_at)}</span>
              </div>
              <div class={css.detailItem}>
                <span class={css.detailLabel}>Сумма</span>
                <span class={css.detailValue}>{numberFormat(order.total_price)} ₽</span>
              </div>
              <div class={css.detailItem}>
                <span class={css.detailLabel}>Оплата</span>
                <span class={css.detailValue}>
                  {order.payment_type === 'bank' ? 'Безналичный' : 'Наличные'}
                </span>
              </div>
              {order.delivery_address && (
                <div class={css.detailItem}>
                  <span class={css.detailLabel}>Адрес доставки</span>
                  <span class={css.detailValue}>{order.delivery_address.address}</span>
                </div>
              )}
            </div>
            {order.comment && (
              <p>
                <strong>Комментарий:</strong> {order.comment}
              </p>
            )}
            <h4>Товары в заказе</h4>
            {order.items?.length ? (
              <ItemsTable
                items={order.items}
                onImage={(src) => setLightboxSrc(src)}
                onMarking={(item) => setMarkingItem(item)}
                onProduct={(sku) => setProductSku(sku)}
              />
            ) : (
              <p class="empty-state">Нет товаров</p>
            )}

            <DocumentsBlock available={order.available_documents} onDownload={handleDownload} />

            <ActionsBlock
              order={order}
              busy={busy}
              onCancel={handleCancel}
              onReqCancel={() => handleChangeRequest('cancel')}
              onReqModify={() => handleChangeRequest('modify')}
            />
          </>
        )}
      </Modal>

      {lightboxSrc && <Lightbox src={lightboxSrc} alt="Фото товара" onClose={() => setLightboxSrc(null)} />}
      {markingItem && <MarkingModal item={markingItem} onClose={() => setMarkingItem(null)} />}
      {productSku && (
        <ProductModal sku={productSku} onClose={() => setProductSku(null)} />
      )}
    </>
  );
}

function ItemsTable({ items, onImage, onMarking, onProduct }) {
  const totalPositions = items.length;
  const totalQty = items.reduce((s, i) => s + (i.quantity ?? 0), 0);
  const totalSum = items.reduce((s, i) => s + (i.price ?? 0) * (i.quantity ?? 0), 0);

  // На мобиле таблица из 7 колонок не помещается — рендерим карточки.
  if (isMobile.value) {
    return (
      <div class={css.itemsCards}>
        {items.map((item, idx) => {
          const imgSrc = item.product?.image_url ? absoluteImageUrl(item.product.image_url) : null;
          const mcCount = item.marking_codes?.length || 0;
          return (
            <div class={css.itemCard} key={`${item.sku}-${idx}`}>
              <div class={css.itemTop}>
                <button
                  type="button"
                  class={`${css.itemThumb} ${!imgSrc ? css.itemThumbEmpty : ''}`}
                  onClick={() => imgSrc && onImage(imgSrc)}
                  disabled={!imgSrc}
                  aria-label={imgSrc ? 'Посмотреть фото' : 'Нет фото'}
                >
                  {imgSrc ? (
                    <img src={imgSrc} alt="" loading="lazy" />
                  ) : (
                    <i class="fa-solid fa-image" aria-hidden="true" />
                  )}
                </button>
                <div class={css.itemMain}>
                  <button type="button" class={css.itemName} onClick={() => onProduct(item.sku)}>
                    {item.name}
                  </button>
                  <span class={css.itemSku}>Артикул: {item.sku}</span>
                  <div class={css.itemPrice}>
                    <span>
                      {item.quantity} шт. × {numberFormat(item.price)} ₽
                    </span>
                    <strong>{numberFormat(item.price * item.quantity)} ₽</strong>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class={`${css.itemMarking} ${mcCount === 0 ? css.itemMarkingEmpty : ''}`}
                onClick={() => mcCount > 0 && onMarking(item)}
                disabled={mcCount === 0}
              >
                <i class="fa-solid fa-barcode" aria-hidden="true" />
                {mcCount > 0 ? `Коды маркировки · ${mcCount}` : 'Коды маркировки появятся после отгрузки'}
              </button>
            </div>
          );
        })}
        <div class={css.itemTotal}>
          <span>
            Итого: {totalPositions} поз. · {totalQty} шт.
          </span>
          <strong>{numberFormat(totalSum)} ₽</strong>
        </div>
      </div>
    );
  }

  return (
    <table class={css.itemsTable}>
      <thead>
        <tr>
          <th />
          <th />
          <th>Артикул</th>
          <th>Название</th>
          <th style={{ textAlign: 'center' }}>Кол-во</th>
          <th style={{ textAlign: 'right' }}>Цена</th>
          <th style={{ textAlign: 'right' }}>Сумма</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => {
          const imgSrc = item.product?.image_url ? absoluteImageUrl(item.product.image_url) : null;
          const mcCount = item.marking_codes?.length || 0;
          return (
            <tr key={`${item.sku}-${idx}`}>
              <td class={css.tdIcon}>
                <button
                  type="button"
                  class={`${css.iconBtn} ${!imgSrc ? css.iconDim : ''}`}
                  title={imgSrc ? 'Посмотреть фото' : 'Нет фото'}
                  onClick={() => imgSrc && onImage(imgSrc)}
                  disabled={!imgSrc}
                  aria-label="Фото"
                >
                  📷
                </button>
              </td>
              <td class={css.tdIcon}>
                <button
                  type="button"
                  class={`${css.iconBtn} ${mcCount === 0 ? css.iconDim : ''}`}
                  title={mcCount > 0 ? `Коды маркировки (${mcCount})` : 'Коды появятся после отгрузки'}
                  onClick={() => onMarking(item)}
                  aria-label="Коды маркировки"
                >
                  <i class="fa-solid fa-barcode" />
                </button>
              </td>
              <td>{item.sku}</td>
              <td>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onProduct(item.sku);
                  }}
                >
                  {item.name}
                </a>
              </td>
              <td style={{ textAlign: 'center' }}>{item.quantity}</td>
              <td style={{ textAlign: 'right' }}>{numberFormat(item.price)} ₽</td>
              <td style={{ textAlign: 'right' }}>{numberFormat(item.price * item.quantity)} ₽</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr class={css.tfootRow}>
          <td colSpan={2} />
          <td colSpan={2}>Итого: {totalPositions} поз.</td>
          <td style={{ textAlign: 'center' }}>{totalQty} шт.</td>
          <td />
          <td style={{ textAlign: 'right' }}>{numberFormat(totalSum)} ₽</td>
        </tr>
      </tfoot>
    </table>
  );
}

function DocumentsBlock({ available, onDownload }) {
  if (!available?.invoice && !available?.upd) return null;
  return (
    <div class={css.docs}>
      <h4>Документы</h4>
      <div class={css.docsList}>
        {available.invoice && (
          <button type="button" class="btn btn-secondary" onClick={() => onDownload('invoice')}>
            <PdfIcon /> <span>Скачать счёт</span>
          </button>
        )}
        {available.upd && (
          <button type="button" class="btn btn-secondary" onClick={() => onDownload('upd')}>
            <PdfIcon /> <span>Скачать УПД</span>
          </button>
        )}
      </div>
    </div>
  );
}

function ActionsBlock({ order, busy, onCancel, onReqCancel, onReqModify }) {
  if (order.pending_change_request) {
    const typeRu = order.pending_change_request.type === 'cancel' ? 'отмену' : 'изменение';
    return (
      <div class={css.actions}>
        <div class={css.pendingRequest}>
          Запрос на {typeRu} отправлен менеджеру. Ожидает обработки.
        </div>
      </div>
    );
  }
  const zone = order.action_zone || 'red';
  if (zone === 'green') {
    return (
      <div class={css.actions}>
        <div class={css.actionsBtns}>
          <button type="button" class="btn btn-danger" disabled={busy} onClick={onCancel}>
            Отменить заказ
          </button>
        </div>
      </div>
    );
  }
  if (zone === 'yellow') {
    return (
      <div class={css.actions}>
        <div class={css.actionsBtns}>
          <button type="button" class="btn btn-warning" disabled={busy} onClick={onReqCancel}>
            Запрос на отмену
          </button>
          <button type="button" class="btn btn-warning" disabled={busy} onClick={onReqModify}>
            Запрос на изменение
          </button>
        </div>
        <p class={css.actionsHint}>
          В этом статусе действия требуют согласования менеджера.
        </p>
      </div>
    );
  }
  return null;
}
